import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { BookCard } from './BookCard';
import { booksService, type ListBooksFilters } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { BookView } from '@/types/api';

/**
 * BookCarousel — horizontal scroll strip of BookCards used by the Library
 * page (explore carousels), the Book Detail modal (similar books), and the
 * search results section.
 *
 * Two modes:
 * - **sync**  — caller passes a preloaded `books` array. No fetching; the
 *   carousel just renders. Used for search results, similar books, and
 *   the "For <Reader>" personalized shelf.
 * - **async** — caller passes `filters`; the carousel fetches 10 random
 *   books matching those filters when it enters the viewport. An
 *   IntersectionObserver deferrals the request until the strip is close
 *   to being scrolled into view so the initial Library render doesn't
 *   fan out to 14 simultaneous book queries.
 *
 * The chevron buttons scroll by the container's visible width (minus a
 * small overlap) so the user can page through without losing track of
 * where they were.
 */

interface SyncProps {
  mode: 'sync';
  books: BookView[];
  /** Optional loading marker for sync mode (search in flight). */
  loading?: boolean;
}

interface AsyncProps {
  mode: 'async';
  filters: ListBooksFilters;
  /** Number of books to fetch. Defaults to 10 per docs/LIBRARY.md. */
  limit?: number;
}

type ModeProps = SyncProps | AsyncProps;

export type BookCarouselProps = ModeProps & {
  /**
   * Strip headline rendered above the books. Optional so callers that
   * already render their own surrounding heading (e.g. the Library
   * page's search-results section) can reuse the carousel without a
   * redundant title/kicker stack.
   */
  title?: string;
  kicker?: string;
  onSelectBook: (book: BookView) => void;
  /** Slot for right-aligned metadata (e.g. "Explore more" link). */
  action?: React.ReactNode;
  /**
   * Message shown when the carousel has no books to render. Defaults to a
   * generic catalogue-filling hint; callers like the Personalized Shelf
   * override it to point at the underlying cause (e.g. missing reader
   * interests) so the user knows what action to take.
   */
  emptyMessage?: string;
  /**
   * Optional per-card action overlay, absolute-positioned in the top-right
   * of each card — mirrors `BookGrid`'s prop so the Bookshelf can reuse the
   * carousel with its status + remove controls. Only meaningful in sync mode.
   */
  renderBadge?: (book: BookView) => React.ReactNode;
  className?: string;
};

const DEFAULT_EMPTY_MESSAGE = 'No books to show yet — check back after the catalogue fills in.';

const SKELETON_COUNT = 6;

export function BookCarousel(props: BookCarouselProps) {
  const { title, kicker, onSelectBook, action, emptyMessage, renderBadge, className } = props;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Async fetch state. Sync mode treats these as a pass-through.
  const [asyncBooks, setAsyncBooks] = useState<BookView[]>([]);
  const [asyncLoading, setAsyncLoading] = useState(props.mode === 'async');
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);

  const isAsync = props.mode === 'async';
  const syncLoading = props.mode === 'sync' ? (props.loading ?? false) : false;
  const books = props.mode === 'sync' ? props.books : asyncBooks;
  const loading = isAsync ? asyncLoading : syncLoading;

  // --- Async: wait until we're near the viewport before fetching. ---
  useEffect(() => {
    if (!isAsync) return;
    if (hasEnteredViewport) return;
    const node = rootRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isAsync, hasEnteredViewport]);

  // --- Async: run the fetch once we've entered the viewport. ---
  useEffect(() => {
    if (!isAsync) return;
    if (!hasEnteredViewport) return;

    const { filters, limit = 10 } = props as AsyncProps;
    let cancelled = false;
    setAsyncLoading(true);

    (async () => {
      try {
        const result = await booksService.list({ ...filters, limit, randomize: true });
        if (!cancelled) setAsyncBooks(result.books);
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load this carousel.');
        setAsyncBooks([]);
      } finally {
        if (!cancelled) setAsyncLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `props` is intentionally stable per mount — Library's configs are
    // module-level constants so we key the fetch off the trigger flags.
  }, [isAsync, hasEnteredViewport]);

  // --- Track scroll position so chevrons can disable at the ends. ---
  const recomputeScrollability = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useLayoutEffect(() => {
    recomputeScrollability();
  }, [books.length, loading]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => recomputeScrollability();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const scrollByViewport = (direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Overlap one card width so a card that was half-in stays visible after the page.
    const delta = Math.max(el.clientWidth - 200, 240);
    el.scrollBy({ left: delta * direction, behavior: 'smooth' });
  };

  const isEmpty = !loading && books.length === 0;

  // Hide the whole strip when empty unless the caller opted into a custom
  // `emptyMessage` (e.g. the Personalized Shelf points users at adding
  // interests). Default empty-state copy was noisy on the Library — every
  // category strip with no matches at the reader's reading level used to
  // render a dashed placeholder. Now those strips disappear.
  if (isEmpty && !emptyMessage) return null;

  return (
    <section ref={rootRef} className={cn('or-carousel-section flex flex-col gap-3', className)}>
      <div className="flex items-end justify-between gap-4">
        {/* Suppress the entire title/kicker block when neither is set —
            callers that wrap the carousel inside their own section header
            (e.g. the Library page's search results) shouldn't get a
            redundant stacked heading. */}
        {title || kicker ? (
          <div>
            {kicker ? <div className="or-kicker">{kicker}</div> : null}
            {title ? (
              <h2 className="font-display text-[20px] font-bold text-or-slate-900">{title}</h2>
            ) : null}
          </div>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {action}
          <div className="flex items-center gap-1.5">
            <ChevronButton
              direction="left"
              onClick={() => scrollByViewport(-1)}
              disabled={!canScrollLeft || loading || isEmpty}
            />
            <ChevronButton
              direction="right"
              onClick={() => scrollByViewport(1)}
              disabled={!canScrollRight || loading || isEmpty}
            />
          </div>
        </div>
      </div>

      {isEmpty ? (
        <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white/60 px-6 py-10 text-center text-[13px] text-or-slate-500">
          {emptyMessage ?? DEFAULT_EMPTY_MESSAGE}
        </div>
      ) : (
        <div
          ref={scrollerRef}
          className="or-scroll-snap flex gap-3 pb-3 -mx-1 px-1 sm:gap-4"
          role="list"
          aria-busy={loading}
        >
          {loading
            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <BookCardSkeleton key={i} />)
            : books.map((book) => (
                <div key={book.id} role="listitem" className="relative snap-start">
                  <BookCard book={book} onSelect={onSelectBook} />
                  {renderBadge ? (
                    <div className="pointer-events-auto absolute right-1.5 top-1.5">
                      {renderBadge(book)}
                    </div>
                  ) : null}
                </div>
              ))}
        </div>
      )}
    </section>
  );
}

function ChevronButton({
  direction,
  onClick,
  disabled,
}: {
  direction: 'left' | 'right';
  onClick: () => void;
  disabled?: boolean;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-700 transition',
        'hover:bg-or-slate-50 disabled:cursor-not-allowed disabled:opacity-40',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

function BookCardSkeleton() {
  return (
    <div className="flex w-[140px] flex-shrink-0 flex-col gap-2 sm:w-[240px]" aria-hidden>
      <div className="aspect-[240/264] w-full animate-pulse rounded-xl bg-or-slate-200" />
      <div className="h-3 w-4/5 animate-pulse rounded bg-or-slate-200" />
      <div className="h-3 w-1/2 animate-pulse rounded bg-or-slate-100" />
    </div>
  );
}
